import React, {useRef, useState} from "react";

import {FileUploadResp} from "shared/types/common";

const useFileUploader = (handleUploadFn: (fl: File) => Promise<FileUploadResp>, currFileName: string, setCurrFileName: React.Dispatch<React.SetStateAction<string>>, currFileLen: number, setCurrFileLen: React.Dispatch<React.SetStateAction<number>>, reUploadMessage: string) => {
    const [file, setFile] = useState<File | null>(null);
    const [status, setStatus] = useState<'idle' | 'uploading' | 'error'>('idle');
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (selectedFile: File | null) => {
        setError(null);
        setStatus('idle');
        if (selectedFile && (selectedFile.name.toLowerCase().endsWith('.csv') || selectedFile.name.toLowerCase().endsWith('.xlsx') || selectedFile.name.toLowerCase().endsWith('.xls') || selectedFile.name.toLowerCase().endsWith('.parquet'))) {
            setFile(selectedFile);
        } else {
            setFile(null);
            if (selectedFile) {
                setError('Please select a .csv, .xlsx or .parquet file.');
                setStatus('error');
            }
        }
    };

    // Updated Upload logic to handle FileUploadResp
    const handleUpload = async () => {
        if (!file || status === 'uploading') return;
        setStatus('uploading');
        setError(null);
        setCurrFileLen(0); // Clear previous count
        setCurrFileName("");
        try {
            const response = await handleUploadFn(file);

            // Check the success flag from the response
            if (response.success) {
                setCurrFileLen(response.rowCount); // Update parent state with row count
                setCurrFileName(response.f_name);
                setFile(null); // Clear selection on success
                setStatus('idle');
                // Optionally display response.message briefly if needed, but clearing is standard
            } else {
                // Handle server-side failure indicated by success: false
                const errorMsg = `Upload failed: ${response.message}${response.errorCode ? ` (Code: ${response.errorCode})` : ''}`;
                setError(errorMsg);
                setStatus('error');
                // Keep file selected on server-side error
            }
        } catch (err: any) {
            // Handle network errors or exceptions during the process
            console.error("Upload process failed:", err);
            setError(err.message || 'Upload failed due to an unexpected error.');
            setStatus('error');
            // Keep file selected on network/client-side error
        }
    };

    const displayLabel =
        status === 'uploading' ? 'Processing...' :
        status === 'error' ? (error || 'Error') : // Show the specific error message
        file ? file.name :
        currFileLen !== 0 ? reUploadMessage :
        'Select or drop CSV file';

    return {
        status,
        inputRef,
        file,
        displayLabel,
        handleFileSelect,
        handleUpload
    };
};

export default useFileUploader;
